// Client mirror of the Ruby DataLoader — fetches JSON reference data from the
// /data endpoints and caches it for the page's life, so every consumer shares a
// single fetch per key.
//
//   import DataLoader from "../lib/data_loader"
//   const roles = await DataLoader.systemGet("roles")   // { solo: {name, ...}, ... }
//   const x     = await DataLoader.get("some_shared_key")
//
// Mirrors the server API name-for-name:
//   Ruby DataLoader.system_get(:roles)  <->  JS DataLoader.systemGet("roles")
//   GET /data/system/<key>.json (active game system) / /data/<key>.json (shared)
//
// The data is static within a deployment (baked into the image), so caching hard
// is safe — a stale cache can only be as stale as the running deploy. The cache
// stores the in-flight promise, so concurrent callers dedupe to one request.
// A missing file (404) rejects; callers handle as they see fit.
//
// Transport is JsonFetch.get (the shared JSON-GET primitive); DataLoader adds its OWN
// policy on top — cache-forever + throw-on-failure — layered over JsonFetch's
// policy-neutral response (it surfaces ok/status; the caching layer decides). This is
// the OPPOSITE policy from JsonFetch's blob callers (they degrade to []; DataLoader
// throws) — which is exactly why they're separate concerns sharing one transport.

import JsonFetch from "#lib/json_fetch"

const cache = new Map() // url -> Promise<data>

function load(url) {
  if (!cache.has(url)) {
    cache.set(
      url,
      JsonFetch.get(url).then((res) => {
        if (!res.ok) {
          cache.delete(url) // don't cache a failure — allow a later retry
          throw new Error(`DataLoader: ${url} -> ${res.status ?? res.error}`)
        }
        return res.data
      })
    )
  }
  return cache.get(url)
}

const DataLoader = {
  // Shared, system-agnostic data: /data/<key>.json
  get(key) {
    return load(`/data/${encodeURIComponent(key)}.json`)
  },

  // Active game-system data: /data/system/<key>.json
  systemGet(key) {
    return load(`/data/system/${encodeURIComponent(key)}.json`)
  },

  // Warm the cache without awaiting — call from a controller's connect() so the
  // data is in flight before a consumer needs it; the later get/systemGet then
  // resolves instantly. Fire-and-forget and idempotent: the promise cache means N
  // controllers preloading the same key trigger ONE fetch (the rest get the
  // in-flight promise and stand down). The swallowed rejection is the preload's
  // own tail only — a real get/systemGet caller still sees the failure. Mirrors
  // the getters: preload <-> get (shared), systemPreload <-> systemGet (system).
  preload(key) {
    this.get(key).catch(() => {})
  },

  systemPreload(key) {
    this.systemGet(key).catch(() => {})
  },
}

export default DataLoader
